"use client";

import type { JSX } from "react";
import { Button, Icon, Text, View } from "@/components";

/** Tono visual del estado: condiciona ícono y color por defecto. */
export type StatusTone = "error" | "locked" | "empty" | "info";

export interface StatusMessageProps {
  /** Título principal del estado (ya traducido). */
  title: string;
  /** Detalle o motivo concreto (p. ej. "completá el módulo 4"). */
  detail?: string;
  /** Tono del estado; define ícono y color por defecto. */
  tone?: StatusTone;
  /** Nombre de ícono FontAwesome para reemplazar el del tono. */
  icon?: string;
  /** Etiqueta de la acción sugerida (reintentar, volver, etc.). */
  actionLabel?: string;
  /** Callback de la acción sugerida. */
  action?: () => void;
}

const TONE_DEFAULTS: Record<StatusTone, { icon: string; color: string }> = {
  error: {
    icon: "fa-solid fa-triangle-exclamation",
    color: "bg-destructive/10 text-destructive"
  },
  locked: {
    icon: "fa-solid fa-lock",
    color: "bg-warning/10 text-warning"
  },
  empty: {
    icon: "fa-regular fa-folder-open",
    color: "bg-primary/10 text-primary"
  },
  info: {
    icon: "fa-solid fa-circle-info",
    color: "bg-primary/10 text-primary"
  }
};

/**
 * @name StatusMessage
 * @description Estado transversal de UI para pantallas con datos remotos:
 * error, vacío, bloqueado (regla de avance) o informativo. Cada vista del dominio
 * e-learning lo usa en lugar de dejar pantallas en blanco: comunica el motivo
 * concreto y ofrece una acción sugerida cuando aplica.
 *
 * @param {StatusMessageProps} props - Título, detalle, tono y acción opcional.
 * @returns {JSX.Element} El estado renderizado, centrado en el área de contenido.
 */
const StatusMessage = ({
  title,
  detail,
  tone = "info",
  icon,
  actionLabel,
  action
}: StatusMessageProps): JSX.Element => {
  const defaults = TONE_DEFAULTS[tone];

  return (
    <View className="flex flex-col items-center justify-center gap-4 py-16">
      <View
        className={`flex items-center justify-center w-16 h-16 rounded-full ${defaults.color}`}
      >
        <Icon name={icon || defaults.icon} className="text-2xl" />
      </View>
      <View className="flex flex-col items-center gap-1 text-center">
        <Text className="font-semibold text-foreground">{title}</Text>
        {detail && (
          <Text className="text-sm text-muted-foreground max-w-md">
            {detail}
          </Text>
        )}
      </View>
      {action && actionLabel && (
        <Button
          action={action}
          label={actionLabel}
          className="px-6 py-2.5 sm:py-2 rounded-full bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
        >
          {actionLabel}
        </Button>
      )}
    </View>
  );
};

export default StatusMessage;
